/**
 * Sortable Table
 *
 * @module
 */

import { $$ } from '../../../utilities/QuerySelectors.js';

export class SortableTable {
    /**
     * Sort the table rows by clicks on the column headings.
     *
     * @param {Element} table
     */
    constructor(table) {
        this.table = table;
        this.headings = $$('th:not(.not-sortable)', table.querySelector('thead'));
        this.lang = ('languages' in navigator) ? navigator.languages[0] : navigator.language;
    }

    /**
     * Add arrow icons to indicate sorting order.
     *
     * @private
     * @return {void}
     */
    addIcons() {
        const span = document.createElement('span');
        span.innerHTML = [ 'leftright', 'arrow-up', 'arrow-down' ].map(s => `<span class="dashicons dashicons-${ s }"></span>`).join('');
        span.className = 'sortable-table__icons';
        this.headings.forEach(th => th.appendChild(span.cloneNode(true)));
    }

    /**
     * Get the current sorting order of a column.
     *
     * @private
     * @param {number} colIndex
     * @return {(string|undefined)}
     */
    getColumnOrder(colIndex) {
        if (this.headings[colIndex].classList.contains('order-desc')) {
            return 'desc';
        }
        if (this.headings[colIndex].classList.contains('order-asc')) {
            return 'asc';
        }
        return undefined;
    }

    /**
     * Sorting comparator.
     *
     * Algorithm courtesy of https://fuzzytolerance.info/blog/2019/07/19/The-better-way-to-do-natural-sort-in-JavaScript/
     *
     * @private
     * @param {any} contentA Data point 1
     * @param {any} contentB Data point 2
     * @return {number}
     */
    compare(contentA, contentB) {
        if (contentA === undefined) {
            return contentB === undefined ? 0 : 1;
        }
        if (contentB === undefined) {
            return contentA === undefined ? 0 : -1;
        }
        if ((typeof contentA === 'number') && (typeof contentB === 'number')) {
            return contentA < contentB ? -1 : 1;
        }
        if (contentA.indexOf('-') === 0 || contentB.indexOf('-') === 0) {
            return parseFloat(contentA) < parseFloat(contentB) ? -1 : 1;
        }
        return contentA.localeCompare(
            contentB,
            navigator.languages[0] || navigator.language,
            {
                numeric: true,
                ignorePunctuation: true,
            }
        );
    }

    /**
     * Filter the table cell content to avoid sort-busting special characters.
     *
     * @private
     * @param {Element} td
     * @return {string} The filtered HTML ready for comparison.
     */
    getSortableContent(td) {
        if (td.textContent === '') {
            return undefined;
        }
        const time = td.getAttribute('data-timestamp');
        if (time) {
            return String(parseInt(time, 10));
        }
        return td.textContent
            .replace('+', '')
            .replace('%', '')
            .replace('$', '')
            .replace(/,/g, '');
    }

    /**
     * Change the order of rows by column sorting.
     *
     * @private
     * @param {number} colIndex
     * @param {string} order
     * @return {void}
     */
    sort(colIndex, order) {
        const body = this.table.querySelector('tbody');
        this.headings.forEach(th => th.classList.remove('order-asc', 'order-desc'));
        this.headings[colIndex].classList.add('order-' + order);
        const items = $$(`td:nth-child(${ colIndex + 1 })`, body)
            .map((td, i) => ({ content: this.getSortableContent(td), i }))
            .sort((a, b) => this.compare(a.content, b.content))
            .map(item => body.children[item.i]);
        if (order === 'desc') {
            items.reverse();
        }
        items.forEach(row => body.appendChild(row));
        this.table.dispatchEvent(new Event('tablesort'));
        this.table.dispatchEvent(new Event('htmlchange'));
    }

    /**
     * Sort the table by the clicked column. Invert if already sorted.
     *
     * @private
     * @param {MouseEvent} e
     * @return {void}
     */
    onHeadingClick(e) {
        /** @type {Element} */ // @ts-ignore: Casting in docblocks is ugh.
        const target = e.currentTarget;

        if (target.classList.contains('not-sortable')) {
            return;
        }

        const colIndex = this.headings.indexOf(target);
        this.sort(colIndex, this.getColumnOrder(colIndex) === 'asc' ? 'desc' : 'asc');
        console.log('SortableTable.onHeadingClick', colIndex);
    }

    /**
     * Add timestamps to date values for easier sorting.
     *
     * @private
     * @return {void}
     */
    addTimestamps() {
        $$('.full-date', this.table).forEach(span => {
            span.parentElement.setAttribute(
                'data-timestamp',
                String((new Date(span.textContent.trim() + 'Z')).getTime() * 0.001)
            );
        });
    }

    /**
     * Initialize the table sorting feature.
     *
     * 1. Initial CSS classes.
     * 2. Arrow icons to indicate sorting state.
     * 3. Event handler to sort on heading click.
     * 4. Extra data needed for sorting.
     * 5. Redo sorting when new data is added to the table.
     *
     * @return {void}
     */
    init() {
        this.table.classList.add('sortable-table');
        this.addIcons();
        this.headings.forEach(th => th.addEventListener('click', this.onHeadingClick.bind(this)));
        this.addTimestamps();
        this.table.addEventListener('datachange', () => this.addTimestamps());
    }
}
